import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

/**
 * THE TYPEAHEAD, once — GEN-50's visible half.
 *
 * `GEN-50`: *"Every 'Pick from list' field must offer a search box inside its
 * dropdown once the list holds more than 10 records."* Twenty pickers in this
 * product held the WHOLE list instead, because that search box did not exist:
 * the item pickers of ~15 document editors shipped 50,000 rows into a
 * `<select>`, and three paging waves in a row declined to page the routes
 * behind them, each writing down the same reason —
 *
 *   *"paging them today would make an item unpickable with nothing on screen
 *   to say so, which is worse than slow."*
 *
 * That reasoning is why this component exists, and it is also the
 * specification for it. Four things here are not decoration:
 *
 *  1. **A SELECTED VALUE OUTSIDE THE CURRENT RESULTS STILL DISPLAYS.**
 *     `selected` is a prop of its own, resolved by id (the `ids` read in
 *     `pagination.ts`), and it is what the closed control shows. Without it a
 *     saved line whose item does not match the search box would render BLANK
 *     — the same defect as the truncated `<select>`, arrived at from the other
 *     side, and the reason a naive typeahead is worse than the bug it
 *     replaces. It is drawn even when the record is switched off, because
 *     BR-MD-01-07 governs what a picker OFFERS, never what it DISPLAYS.
 *
 *  2. **AN EMPTY RESULT SAYS SO.** A panel that renders nothing reads as a
 *     broken control, and the spec asks for the sentence by name — FIN-03
 *     §5's picker: *"Empty result reads **No matching customer**"*. Callers
 *     pass that sentence; the default says nothing matched rather than
 *     showing nothing.
 *
 *  3. **THE KEYBOARD IS THE CONTROL, not an enhancement.** A `<select>` came
 *     with arrows, Enter, Escape and type-ahead for free; anything replacing
 *     it owes the same. Arrow keys move the active row, Enter takes it,
 *     Escape closes without changing the value, Tab leaves it alone.
 *
 *  4. **RTL IS A LAYOUT, NOT A TRANSLATION.** Logical properties only
 *     (`ps-*`/`pe-*`/`text-start`), no `left`/`right`, so the panel and its
 *     rows flip with the document rather than being mirrored by hand.
 *
 * The data half is deliberately NOT here: this component never fetches. It is
 * given `options` (one page of matches, `PICKER_PAGE_SIZE`) and `selected`
 * (the record the form holds), and it reports what the user typed. That is
 * what lets one component serve items, partners, orders, bills and jobs
 * without knowing what any of them are.
 */

/**
 * GEN-51's row, spec line 3767: *"Every 'Pick from list' field must offer a
 * '＋ Create new' choice at the bottom of its dropdown, if the user's role
 * allows creating that record."*
 *
 * It is a THIRD category of row, never an entry appended to `options`, and the
 * distinction is the same one the empty-state notice below is built on: a
 * search result is something the query FOUND, and this is something the
 * control always OFFERS. Folding it into `options` would make
 * `options.length` non-zero for a search that matched nothing, which is
 * exactly the bug the long comment at the foot of this file records — the
 * clear row hiding "nothing matched" — arrived at from the other end.
 *
 * `label` is supplied by the caller rather than read from the dictionary here,
 * because this component names nothing: it serves partners, items, orders and
 * bills without knowing what any of them are, and the row that creates one is
 * no different.
 *
 * WHOSE JOB THE PERMISSION IS. Not this component's. GEN-51's "if the user's
 * role allows" is decided by the caller, which passes no `createNew` at all
 * when the role cannot create — so there is no row to walk onto, name, or
 * press. That is deliberately ABSENT rather than `Gated`'s greyed control:
 * BR-PERM-18 governs BUTTONS on a screen ("the button must never vanish"),
 * while this is a row inside a popup that only exists while it is open, and
 * GEN-51 states its own condition in the rule text. A greyed row inside a
 * listbox would also have to stay in the arrow-key walk to be announced, which
 * makes "press Enter to do nothing" the accessible experience.
 */
export interface TypeaheadCreateNew {
  /** The row's text — GEN-51's "＋ Create new". */
  label: string;
  /**
   * Chosen. The control closes its panel and changes NOTHING: creating a
   * record is an act, not a selection, and the field keeps whatever it held
   * until the new record actually exists.
   */
  onSelect: () => void;
}

export interface TypeaheadOption {
  id: string;
  /** The line a reader scans: an item's name, "PO-0001 — Northwind Traders". */
  label: string;
  /** The quieter second line: a code, a status, a balance. Never the only difference between two rows. */
  hint?: string | undefined;
  /** Switched off. Still selectable when already chosen; drawn as what it is. */
  inactive?: boolean | undefined;
}

export interface TypeaheadProps {
  /** The chosen id, or null. */
  value: string | null;
  onChange: (id: string | null) => void;
  /** One page of matches for `query` — never the whole list. */
  options: readonly TypeaheadOption[];
  /**
   * The record `value` names, EVEN WHEN IT IS NOT IN `options`. See (1) above:
   * this is the prop that makes the conversion safe.
   */
  selected?: TypeaheadOption | null | undefined;
  /** What the user has typed, hoisted so the caller can ask the server for it. */
  query: string;
  onQueryChange: (query: string) => void;
  /** Names the control for a screen reader — and for every test that finds it. */
  label: string;
  placeholder?: string | undefined;
  /** The spec's own sentence where it names one ("No matching customer"). */
  emptyLabel?: string | undefined;
  /**
   * The "no record" row, where choosing nothing is a legal answer — a
   * document grid's free-text line, an optional link. Absent = no such row.
   */
  clearLabel?: string | undefined;
  /** GEN-51's "＋ Create new" row. Absent = the role may not create, or the
   *  field points at something nobody creates from here. */
  createNew?: TypeaheadCreateNew | undefined;
  disabled?: boolean | undefined;
  /** True while the caller's query is in flight; the panel says so. */
  loading?: boolean | undefined;
  /**
   * False while `options` are not yet the answer to `query` — the debounce
   * has not fired or the request is still out. Enter pressed in that gap
   * WAITS for the answer instead of taking a stale row: a barcode scanner
   * types the code and presses Enter faster than any search returns.
   * Absent = always settled (a caller filtering locally).
   */
  settled?: boolean | undefined;
  id?: string | undefined;
  className?: string | undefined;
  /** Grid cells want a shorter control than a form field. */
  size?: 'default' | 'cell' | undefined;
}

export function Typeahead({
  value,
  onChange,
  options,
  selected,
  query,
  onQueryChange,
  label,
  placeholder,
  emptyLabel,
  clearLabel,
  createNew,
  disabled,
  loading,
  settled = true,
  id,
  className,
  size = 'default',
}: TypeaheadProps) {
  const { t } = useTranslation();
  const reactId = React.useId();
  const listId = `${id ?? reactId}-list`;
  const [open, setOpen] = React.useState(false);
  const [active, setActive] = React.useState(0);
  const inputRef = React.useRef<HTMLInputElement>(null);

  /**
   * The rows the panel actually draws. The clear row is one of them rather
   * than a button beside them, so that one arrow-key walk covers every choice
   * the control offers — including "none".
   */
  const rows: (TypeaheadOption | null)[] = React.useMemo(
    () => (clearLabel === undefined ? [...options] : [null, ...options]),
    [clearLabel, options],
  );

  /**
   * The create row is LAST, which is GEN-51's "at the bottom" and is also why
   * it cannot live in `rows`: the clear row is first by construction there,
   * and `rows` is what the empty-state notice is deliberately NOT counted
   * against. So it gets one index past the end and joins the walk by
   * arithmetic instead — `rowCount`, not `rows.length`, is what the arrow
   * keys, the modulo and `aria-activedescendant` all read.
   *
   * `createIndex` is -1 when there is no such row, which no `active` can ever
   * equal, so every branch below collapses to the old behaviour for the fifty
   * call sites that pass nothing.
   */
  const createIndex = createNew === undefined ? -1 : rows.length;
  const rowCount = rows.length + (createNew === undefined ? 0 : 1);

  /**
   * Whether the reader has MOVED the cursor since the results last changed.
   * The clear row is row 0, so without this a typed search followed by Enter
   * took "free-text line" and emptied the field (bugs: Sales › Invoices ›
   * Bug #1 — "search the item by code, press Enter, the cell goes blank").
   */
  const walked = React.useRef(false);
  const searching = query.trim() !== '';
  /** Enter arrived before the results did; take the first match once they land. */
  const pendingEnter = React.useRef(false);

  React.useEffect(() => {
    // A new result set invalidates the old cursor: keeping index 4 after the
    // list shrank to two rows is how Enter selects something nobody looked at.
    // With a typed search the cursor starts on the first MATCH, not on the
    // clear row in front of it — Enter after typing a code takes that code.
    walked.current = false;
    setActive(clearLabel !== undefined && searching && options.length > 0 ? 1 : 0);
    // `searching` is deliberately not a dependency: the cursor resets when the
    // RESULTS change, which is what a typed character eventually produces.
  }, [options, clearLabel]);

  React.useEffect(() => {
    if (!pendingEnter.current || !settled) return;
    pendingEnter.current = false;
    const first = options[0];
    if (first !== undefined) commit(first);
    // `commit` is recreated every render and reads only props; the effect
    // fires on the two facts that decide it.
  }, [options, settled]);

  const displayed = open ? query : (selected?.label ?? '');

  function commit(option: TypeaheadOption | null) {
    onChange(option?.id ?? null);
    onQueryChange('');
    setOpen(false);
    inputRef.current?.focus();
  }

  /**
   * The create row, taken. Deliberately NOT `commit` with some sentinel:
   * `onChange` is never called, so a field holding a customer still holds that
   * customer while the window is open and still holds it if the window is
   * cancelled. The only way this field's value changes is a record that exists.
   *
   * No `inputRef.focus()` either — the window that opens next wants the focus,
   * and taking it back here would race whatever that window focuses first. The
   * return trip is the caller's, and it is why the input still has focus at the
   * moment this runs (the row `preventDefault`s its own mousedown, and Enter
   * never moved it), so a caller can read `document.activeElement` and put it
   * back when the window closes.
   */
  function chooseCreate() {
    onQueryChange('');
    setOpen(false);
    createNew?.onSelect();
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      if (rowCount === 0) return;
      walked.current = true;
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setActive((current) => (current + step + rowCount) % rowCount);
      return;
    }
    if (event.key === 'Enter') {
      if (!open) return;
      if (searching && !settled && !walked.current) {
        event.preventDefault();
        pendingEnter.current = true;
        return;
      }
      // The create row is one of the things Enter can take — GEN-51's row is
      // reachable by keyboard alone or it is not offered to a keyboard user
      // at all.
      if (active === createIndex) {
        event.preventDefault();
        chooseCreate();
        return;
      }
      // Only swallow the key when there is something to take — otherwise a
      // form's own Enter (submit, next cell) stops working inside a picker.
      const row = rows[active];
      if (row === undefined) return;
      event.preventDefault();
      // A typed search that matched nothing is not a request to EMPTY the
      // field. The clear row is still one arrow away for whoever means it.
      if (row === null && searching && !walked.current) return;
      commit(row);
      return;
    }
    if (event.key === 'Escape') {
      if (!open) return;
      event.preventDefault();
      // Escape abandons the search and keeps the value — the `<select>`
      // behaviour this replaced.
      onQueryChange('');
      setOpen(false);
      return;
    }
    if (event.key === 'Tab') setOpen(false);
  }

  return (
    <div
      className={cn('relative', className)}
      onBlur={(event) => {
        // Closing on blur, unless the focus went INSIDE the panel — otherwise
        // clicking a row closes the list before the click lands on it.
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setOpen(false);
          onQueryChange('');
        }
      }}
    >
      <input
        ref={inputRef}
        id={id}
        type="text"
        role="combobox"
        aria-label={label}
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && rowCount > 0 ? `${listId}-${active}` : undefined}
        autoComplete="off"
        disabled={disabled}
        value={displayed}
        placeholder={selected ? undefined : (placeholder ?? t('picker.placeholder'))}
        onChange={(event) => {
          setOpen(true);
          pendingEnter.current = false;
          onQueryChange(event.target.value);
        }}
        onFocus={() => setOpen(true)}
        onMouseDown={() => setOpen(true)}
        onKeyDown={onKeyDown}
        data-slot="typeahead"
        className={cn(
          'flex w-full min-w-0 rounded-[11px] border border-input bg-transparent px-3 py-1 text-start transition-colors outline-none',
          'placeholder:text-muted-foreground',
          'disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50',
          'focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/50',
          'aria-invalid:border-destructive aria-invalid:ring-destructive/20',
          size === 'cell' ? 'h-9 text-sm' : 'h-12 text-base',
          selected?.inactive ? 'text-muted-foreground' : undefined,
        )}
      />

      {open ? (
        <ul
          id={listId}
          role="listbox"
          /*
           * NO `aria-label` HERE, and that is a fix rather than an omission.
           *
           * It carried the field's own label, which gave the page TWO nodes
           * with the accessible name "Customer" — the box and its panel — so
           * `getByLabel('Customer')` became ambiguous the moment the panel
           * opened. Fifteen journeys failed on exactly that. A combobox's
           * popup is named by the combobox that owns it (`aria-controls`
           * points at this element and `aria-activedescendant` at the row
           * inside it); giving it a second copy of the name adds nothing for
           * a screen reader and one duplicate match for everything else.
           */
          /*
           * §4.3's panel: as wide as the box, scrolling inside itself rather
           * than pushing the page. `start-0` and `end-0` rather than
           * `left/right`, so it lands under the box in both directions.
           */
          className="absolute start-0 end-0 z-50 mt-1 max-h-[280px] overflow-y-auto rounded-[11px] border border-input bg-popover py-1 text-sm shadow-lg"
        >
          {rows.map((row, index) => (
            <li
              /*
               * The clear row's key is a sentinel that no option id can equal.
               * WRITTEN AS AN ESCAPE, AND THAT IS THE WHOLE POINT: it used to
               * be a LITERAL NUL byte in the source, which makes BSD grep
               * classify this file as binary — so `grep` printed NOTHING for
               * it and exited 1, indistinguishable from "no match here", and
               * a multi-file grep skipped it while still exiting 0. A file
               * that silently disappears from every search is a worse defect
               * than anything it could contain. `\u0000` produces the
               * identical character at runtime and leaves the file greppable.
               */
              key={row?.id ?? '\u0000none'}
              id={`${listId}-${index}`}
              role="option"
              aria-selected={row === null ? value === null : row.id === value}
              // The row takes the click, never the focus: `preventDefault`
              // on mousedown keeps the caret in the box so the panel does
              // not close underneath the pointer.
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => commit(row)}
              onMouseEnter={() => setActive(index)}
              className={cn(
                'cursor-pointer px-3 py-2 text-start',
                index === active ? 'bg-accent text-accent-foreground' : undefined,
              )}
            >
              {row === null ? (
                <span className="text-muted-foreground">{clearLabel}</span>
              ) : (
                <>
                  <span className={row.inactive ? 'text-muted-foreground' : undefined}>
                    {row.label}
                  </span>
                  {row.hint === undefined ? null : (
                    <span className="ms-2 text-xs text-muted-foreground">{row.hint}</span>
                  )}
                </>
              )}
            </li>
          ))}

          {/*
           * THE NOTICE IS ABOUT `options`, NOT ABOUT `rows` — this is the
           * line that actually delivers (2) in the file header above.
           *
           * It used to be the other arm of `rows.length === 0`, and `rows` is
           * `[null, ...options]` whenever a `clearLabel` is given. So for
           * every picker that offers a "no record" row — a document line's
           * free-text row, "All warehouses", "myself" — the panel could never
           * BE empty, and both sentences below were unreachable: a search
           * that matched nothing drew the clear row ALONE, which reads as
           * "here is your one result" rather than "nothing matched". The
           * `loading` notice shared that dead branch and was equally
           * unsayable.
           *
           * The clear row is not a search result. It is an action the control
           * always offers, so it can neither satisfy a search nor suppress
           * the news that the search found nothing. Hence `options.length`:
           * the notice is a statement about what came back for `query`, and
           * it sits BELOW the rows, so a caller whose clear row must come
           * first (the reports filters' "All is the first choice") keeps it
           * there.
           *
           * `role="presentation"` is load-bearing, and asserted rather than
           * assumed: this is a line to READ, never one more thing to arrow
           * onto and commit. Keeping it out of `rows` is what keeps
           * `aria-activedescendant`, Enter and the modulo walk honest.
           */}
          {options.length === 0 ? (
            <li
              // Not an option: there is nothing here to choose. A screen
              // reader hears it because the panel it sits in has just opened.
              role="presentation"
              className="px-3 py-2 text-muted-foreground"
            >
              {loading ? t('picker.searching') : (emptyLabel ?? t('picker.noMatch'))}
            </li>
          ) : null}

          {/*
           * GEN-51's row, and it sits BELOW the notice on purpose.
           *
           * "At the bottom of its dropdown" is the rule's own wording, and the
           * bottom is the bottom: a search that matched nothing should read
           * "Nothing matched your search." and then offer to make the thing.
           * Putting the row above the notice would read as a result the search
           * returned, which is the same conflation the notice comment above
           * spends twenty lines undoing for the clear row.
           *
           * `role="option"` is not decorative and not a lie about what it is.
           * `aria-activedescendant` may only point at an element with that
           * role, so a row the arrow keys can reach MUST carry it — a
           * `role="presentation"` create row would be walked onto and
           * announced as nothing. `aria-selected={false}` says the rest: it is
           * choosable, and it is never the field's current value.
           */}
          {createNew === undefined ? null : (
            <li
              id={`${listId}-${createIndex}`}
              role="option"
              aria-selected={false}
              onMouseDown={(event) => event.preventDefault()}
              onClick={chooseCreate}
              onMouseEnter={() => setActive(createIndex)}
              className={cn(
                'cursor-pointer border-t border-input px-3 py-2 text-start font-medium',
                createIndex === active ? 'bg-accent text-accent-foreground' : undefined,
              )}
            >
              {createNew.label}
            </li>
          )}
        </ul>
      ) : null}
    </div>
  );
}
